import { isValid } from "date-fns";
import { useCallback, useState } from "react";
import { MenuItem, Select, Stack } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// AdapterDateFnsV3 was the v7-era shim for date-fns 3; x-date-pickers 9
// dropped it, and the plain AdapterDateFns now targets date-fns 3 and 4 --
// which is what this repo is on. This provider is nested inside the app-wide
// AdapterDayjs one in App.tsx, so this subtree alone works in native Date.
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { useApply } from "./hooks";

export type DateThresholdComparator = "lte" | "gte";

export interface DateThresholdFilterValue {
  cutoffDate: Date;
  comparator: DateThresholdComparator;
}

export interface Props {
  onApply: (
    cutoffDate: Date | null | undefined,
    comparator: DateThresholdComparator
  ) => void;
  cutoffDate: Date | null | undefined;
  comparator: DateThresholdComparator | null | undefined;
}

export default function DateThresholdFilter({
  onApply,
  cutoffDate,
  comparator,
}: Props) {
  const [selectedComparator, setSelectedComparator] =
    useState<DateThresholdComparator>(comparator || "lte");
  const [selectedCutoffDate, setSelectedCutoffDate] = useState<Date | null>(
    cutoffDate || null
  );

  const apply = useCallback(() => {
    onApply(selectedCutoffDate, selectedComparator);
  }, [onApply, selectedComparator, selectedCutoffDate]);

  useApply(apply);

  return (
    <Stack sx={{ gap: "0.5em", mt: "1em" }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Select
          value={selectedComparator}
          onChange={(e) => {
            const value = e.target.value;

            if (value === "gte" || value === "lte") {
              setSelectedComparator(value);
            }
          }}
        >
          <MenuItem value="lte">Before</MenuItem>
          <MenuItem value="gte">After</MenuItem>
        </Select>
        <DateTimePicker
          value={selectedCutoffDate}
          onChange={(d) => {
            // Every adapter imported anywhere in the program widens the
            // picker's value type, so with AdapterDayjs also in the tree this
            // is `Dayjs | Date | null` even though the enclosing provider only
            // ever produces a Date. Narrow it rather than assert it.
            setSelectedCutoffDate(d instanceof Date && isValid(d) ? d : null);
          }}
        />
      </LocalizationProvider>
    </Stack>
  );
}
